import React, {FC, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useFormik} from 'formik';
import {lang} from "../../../../../types/lang";
import {LANGUAGES} from "../../../../../mock/languages";
import {useNotification} from "../../../../../context/NotificationContext";
import {createResourceSchema} from "../../../../../schema/resourceSchema";
import {createResource, updateResource} from "../../../../../services/resourceSvc";
import {UploadImage} from "../../../../../components/common/uploadImage/UploadImage";

type ResourceFormProps = {
  selectedResource: any;
  resourceById: any;
  lessonId: string;
  refetchResources: () => void;
  setSelectedResource: (resource: any) => void;
};

const ResourceForm: FC<ResourceFormProps> = ({
                                               selectedResource,
                                               resourceById,
                                               lessonId,
                                               refetchResources,
                                               setSelectedResource
                                             }) => {
  const navigate = useNavigate();
  const {formatMessage} = useIntl();
  const {showNotification} = useNotification();
  const [activeTab, setActiveTab] = useState<lang>('uz');

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: {
        uz: resourceById?.title_uz || '',
        ru: resourceById?.title_ru || '',
        en: resourceById?.title_en || '',
      },
      description: {
        uz: resourceById?.description_uz || '',
        ru: resourceById?.description_ru || '',
        en: resourceById?.description_en || '',
      },
      banner: null,
      file: null,
    },
    validationSchema: !selectedResource ? createResourceSchema : null,
    onSubmit: async (values, {resetForm}) => {
      const formData = new FormData();
      formData.append('title_uz', values.title.uz);
      formData.append('title_ru', values.title.ru);
      formData.append('title_en', values.title.en);
      formData.append('description_uz', values.description.uz);
      formData.append('description_ru', values.description.ru);
      formData.append('description_en', values.description.en);
      formData.append('lesson', lessonId);

      if (values.banner) {
        formData.append('banner', values.banner);
      }

      if (values.file) {
        formData.append('file', values.file);
      }

      try {
        if (selectedResource) {
          const res = await updateResource(selectedResource, formData);
          if (res?.success) {
            showNotification('Muvaffaqiyatli', 'Resurs yangilandi', 'success');
            resetForm();
            refetchResources();
            setTimeout(() => {
              setSelectedResource(null);
            }, 1000);
          }
        } else {
          const res = await createResource(formData);
          if (res?.success) {
            showNotification('Muvaffaqiyatli', 'Resurs qo\'shildi', 'success');
            resetForm();
            setTimeout(() => {
              navigate(-1);
            }, 1000);
          }
        }
      } catch (e) {
        showNotification('Xatolik!', '', 'error');
      }
    },
  });

  const handleTabChange = (lang) => {
    setActiveTab(lang);
  };

  return (
      <div className="card">
        <div className="card-header">
          <h1 className="card-title">Resurs qo'shish</h1>
          <ul className="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0">
            {LANGUAGES.map((lang) => (
                <li className="nav-item" key={lang.key}>
                  <button
                      className={`nav-link ${activeTab === lang.key ? 'active' : ''}`}
                      onClick={() => handleTabChange(lang.key)}
                  >
                    {lang.name}
                  </button>
                </li>
            ))}
          </ul>
        </div>
        <div className="card-body">
          <form className="form" onSubmit={formik.handleSubmit} encType="multipart/form-data">
            <div className="card-body p-9">
              <div className="row mb-6">
                <label className="col-lg-4 col-form-label required fw-bold fs-6">Dars nomi</label>
                <div className="col-lg-8 fv-row">
                <textarea
                    className="form-control form-control-lg form-control-solid"
                    placeholder="Dars nomi"
                    name={`title.${activeTab}`}
                    value={formik.values.title[activeTab]}
                    onChange={formik.handleChange}
                />
                  {formik.touched.title?.[activeTab] && formik.errors.title?.[activeTab] && (
                      <div className="text-danger">{formik.errors.title[activeTab]?.toString()}</div>
                  )}
                </div>
              </div>

              <div className="row mb-6">
                <label className="col-lg-4 col-form-label required fw-bold fs-6">Darslik taglavhasi</label>
                <div className="col-lg-8 fv-row">
                <textarea
                    className="form-control form-control-lg form-control-solid"
                    placeholder="Darslik taglavhasi"
                    name={`description.${activeTab}`}
                    value={formik.values.description[activeTab]}
                    onChange={formik.handleChange}
                />
                  {formik.touched.description?.[activeTab] && formik.errors.description?.[activeTab] && (
                      <div className="text-danger">{formik.errors.description[activeTab]?.toString()}</div>
                  )}
                </div>
              </div>

              <div className="row mb-6">
                <label className="col-lg-4 col-form-label required fw-bold fs-6">Banner rasmi</label>
                <div className="col-lg-8 fv-row">
                  <UploadImage imageSizes="banner" onImageUpload={(file) => {
                    if (file) {
                      formik.setFieldValue('banner', file);
                    }
                  }}/>
                  {formik.touched.banner && formik.errors.banner && (
                      <div className="text-danger">{formik.errors.banner}</div>
                  )}
                </div>
              </div>

              <div className="row mb-6">
                <label className="col-lg-4 col-form-label required fw-bold fs-6">Fayl</label>
                <div className="col-lg-8 fv-row">
                  <input type="file" name="file" onChange={(e) => formik.setFieldValue('file', e.target.files?.[0])}
                         className="form-control"/>
                  {formik.touched.file && formik.errors.file && (
                      <div className="text-danger">{formik.errors.file}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="card-footer">
              <div className="justify-content-end d-flex gap-2">
                <button className="btn btn-secondary" onClick={() => navigate(-1)} type="button">
                  {formatMessage({id: 'Bekor qilish'})}
                </button>
                <button className="btn btn-primary" type="submit" disabled={formik.isSubmitting}>
                  {selectedResource ? 'Yangilash' : 'Saqlash'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
  );
};

export default ResourceForm;